import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  ChefHat,
  Clock,
  Mail,
  MessageSquare,
  Phone,
  Users,
  UtensilsCrossed,
  XCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  useGetServiceRequestByIdQuery,
  useUpdateServiceRequestStatusMutation,
  type ServiceRequest,
  type ServiceRequestStatus,
} from "@/store";
import { toast } from "sonner";

const STATUS_CONFIG: Record<
  ServiceRequestStatus,
  { label: string; color: string; icon: React.ReactNode }
> = {
  PENDING: {
    label: "Pending",
    color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
    icon: <Clock className="h-3 w-3" />,
  },
  CONTACTED: {
    label: "Contacted",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
    icon: <MessageSquare className="h-3 w-3" />,
  },
  IN_PROGRESS: {
    label: "In Progress",
    color: "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
    icon: <Loader2 className="h-3 w-3" />,
  },
  CONFIRMED: {
    label: "Confirmed",
    color: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
    icon: <CheckCircle className="h-3 w-3" />,
  },
  COMPLETED: {
    label: "Completed",
    color: "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
    icon: <CheckCircle className="h-3 w-3" />,
  },
  CANCELLED: {
    label: "Cancelled",
    color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
    icon: <XCircle className="h-3 w-3" />,
  },
};

const getServiceIcon = (type: ServiceRequest["type"]) => {
  return type === "CATERING" ? (
    <UtensilsCrossed className="h-5 w-5 text-amber-500" />
  ) : (
    <ChefHat className="h-5 w-5 text-orange-500" />
  );
};

export function AdminServiceRequestDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetServiceRequestByIdQuery(id || "", {
    skip: !id,
  });
  const [updateStatus, { isLoading: isUpdating }] = useUpdateServiceRequestStatusMutation();

  const request = data?.data;

  const [newStatus, setNewStatus] = useState<ServiceRequestStatus>("PENDING");
  const [adminNotes, setAdminNotes] = useState("");

  useEffect(() => {
    if (request) {
      setNewStatus(request.status);
      setAdminNotes(request.adminNotes || "");
    }
  }, [request]);

  const handleUpdateStatus = async () => {
    if (!request) return;

    try {
      await updateStatus({
        id: request.id,
        data: { status: newStatus, adminNotes },
      }).unwrap();
      toast.success("Status updated successfully");
    } catch (updateError: any) {
      toast.error(updateError?.data?.error || "Failed to update status");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Service request not found</p>
        <Button onClick={() => navigate("/admin/service-requests")}>Back to Requests</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/service-requests")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              {getServiceIcon(request.type)}
              {request.type === "CATERING" ? "Catering Request" : "Private Chef Request"}
            </h1>
            <p className="text-muted-foreground">
              Submitted {format(new Date(request.createdAt), "PPp")}
            </p>
          </div>
        </div>
        <Badge className={STATUS_CONFIG[request.status].color}>
          {STATUS_CONFIG[request.status].icon}
          <span className="ml-1">{STATUS_CONFIG[request.status].label}</span>
        </Badge>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Request Overview</CardTitle>
              <CardDescription>
                Request ID: {request.id.slice(0, 8).toUpperCase()}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {request.email}
                </span>
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {request.phoneNumber}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {request.numberOfGuests} guests
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {request.eventDate || request.serviceDates || "N/A"}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Request Details</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="contact">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="contact">Contact</TabsTrigger>
                  <TabsTrigger value="event">Event Details</TabsTrigger>
                  <TabsTrigger value="service">Service Details</TabsTrigger>
                </TabsList>

                <TabsContent value="contact" className="space-y-4 mt-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label className="text-muted-foreground">Full Name</Label>
                      <p className="font-medium">{request.fullName}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Email</Label>
                      <p className="font-medium">{request.email}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Phone</Label>
                      <p className="font-medium">{request.phoneNumber}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Preferred Contact</Label>
                      <p className="font-medium capitalize">
                        {request.preferredContactMethod}
                      </p>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="event" className="space-y-4 mt-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label className="text-muted-foreground">Date</Label>
                      <p className="font-medium">
                        {request.eventDate || request.serviceDates || "N/A"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Time</Label>
                      <p className="font-medium">
                        {request.eventTime || request.serviceTime || "N/A"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Location</Label>
                      <p className="font-medium">
                        {request.eventLocation || request.location || "N/A"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Number of Guests</Label>
                      <p className="font-medium">{request.numberOfGuests}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Budget Range</Label>
                      <p className="font-medium">{request.budgetRange}</p>
                    </div>
                  </div>
                  {request.specialNotes && (
                    <div>
                      <Label className="text-muted-foreground">Special Notes</Label>
                      <p className="font-medium whitespace-pre-wrap">
                        {request.specialNotes}
                      </p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="service" className="space-y-4 mt-4">
                  {Object.entries(request.serviceDetails || {}).map(([key, value]) => (
                    <div key={key}>
                      <Label className="text-muted-foreground capitalize">
                        {key.replace(/([A-Z])/g, " $1").trim()}
                      </Label>
                      <p className="font-medium">{String(value) || "N/A"}</p>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Update Status</CardTitle>
              <CardDescription>Keep internal notes and change status.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={newStatus}
                  onValueChange={(value) => setNewStatus(value as ServiceRequestStatus)}
                  disabled={isUpdating}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="CONTACTED">Contacted</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Admin Notes</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(event) => setAdminNotes(event.target.value)}
                  placeholder="Add notes about this request..."
                  rows={5}
                />
              </div>

              <Button onClick={handleUpdateStatus} disabled={isUpdating} className="w-full">
                {isUpdating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Status"
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AdminServiceRequestDetailScreen;
